import { useContext, useState, useEffect, useRef, useCallback } from 'react';
import { EnteteGlobaleContext } from './enteteGlobale';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Bell, CheckCheck, Trash2, ShieldAlert,
  CalendarCheck, CalendarX, Calendar, Clock,
  FileSpreadsheet, MessageSquare, AlertTriangle, RefreshCw,
  FileCheck, ThumbsUp, XCircle, Settings, Banknote, Heart,
} from 'lucide-react';

const API   = process.env.REACT_APP_API_URL || '';
const VERT  = '#1B5E20';
const ROUGE = '#C62828';

// ─── Icône par type ────────────────────────────────────────────────────────
const ICON_TYPE = {
  conge_approuve:       <CalendarCheck   size={14} color="#2E7D32" />,
  conge_refuse:         <CalendarX       size={14} color={ROUGE} />,
  conge_en_attente:     <Calendar        size={14} color="#E65100" />,
  pointage_valide:      <Clock           size={14} color="#0277BD" />,
  absence_detectee:     <AlertTriangle   size={14} color="#E65100" />,
  compte_bloque:        <ShieldAlert     size={14} color={ROUGE} />,
  import_termine:       <FileSpreadsheet size={14} color="#6A1B9A" />,
  message_rh:           <MessageSquare   size={14} color={VERT} />,
  retard_detecte:       <Clock           size={14} color="#E65100" />,
  reinitialisation_mdp: <RefreshCw       size={14} color="#0277BD" />,
  alerte_securite:      <ShieldAlert     size={14} color={ROUGE} />,
  pret_soumis:          <FileCheck       size={14} color="#0277BD" />,
  pret_approuve:        <ThumbsUp        size={14} color="#2E7D32" />,
  pret_refuse:          <XCircle         size={14} color={ROUGE} />,
  pret_traite:          <Settings        size={14} color="#E65100" />,
  pret_debloque:        <Banknote        size={14} color="#2E7D32" />,
  ipm_facture:          <Heart           size={14} color="#1565C0" />,
};

const BG_TYPE = {
  conge_approuve:       '#E8F5E9',
  conge_refuse:         '#FFEBEE',
  conge_en_attente:     '#FFF3E0',
  absence_detectee:     '#FFF3E0',
  compte_bloque:        '#FFEBEE',
  alerte_securite:      '#FFEBEE',
  import_termine:       '#F3E5F5',
  reinitialisation_mdp: '#E3F2FD',
  message_rh:           '#E8F5E9',
  retard_detecte:       '#FFF3E0',
  pointage_valide:      '#E3F2FD',
  pret_soumis:          '#E3F2FD',
  pret_approuve:        '#E8F5E9',
  pret_refuse:          '#FFEBEE',
  pret_traite:          '#FFF3E0',
  pret_debloque:        '#E8F5E9',
  ipm_facture:          '#E3F2FD',
};

// ─── Temps relatif ─────────────────────────────────────────────────────────
function tempsRelatif(dateStr) {
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60)    return 'il y a quelques secondes';
  if (diff < 3600)  return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  if (diff < 172800)return 'hier';
  return new Date(dateStr).toLocaleDateString('fr-FR');
}

// ─── Web Audio ─────────────────────────────────────────────────────────────
function jouerBip(urgente = false) {
  try {
    const ctx  = new (window.AudioContext || window.webkitAudioContext)();
    const osc  = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.value = urgente ? 880 : 523;
    gain.gain.setValueAtTime(urgente ? 0.25 : 0.12, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + (urgente ? 0.6 : 0.3));
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + (urgente ? 0.6 : 0.3));
    if (urgente) {
      const osc2 = ctx.createOscillator();
      const g2   = ctx.createGain();
      osc2.connect(g2); g2.connect(ctx.destination);
      osc2.type = 'sine'; osc2.frequency.value = 1047;
      g2.gain.setValueAtTime(0.2, ctx.currentTime + 0.35);
      g2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.75);
      osc2.start(ctx.currentTime + 0.35);
      osc2.stop(ctx.currentTime + 0.75);
    }
  } catch (_) {}
}

function vibrer(urgente = false) {
  if (!navigator.vibrate) return;
  if (urgente) navigator.vibrate([200, 100, 200]);
  else         navigator.vibrate(200);
}

// ═══════════════════════════════════════════════════════════════════════════
// Props :
//   couleurIcone — couleur de l'icône SVG (défaut '#fff')
//   taille       — taille de l'icône en px (défaut 22)
// ═══════════════════════════════════════════════════════════════════════════
// Refonte visuelle : la cloche vit dans l'en-tête général (components/Layout). Les cloches encore
// placées dans les bandeaux de certaines pages ne s'affichent plus sous cet en-tête, pour qu'il
// n'y en ait jamais deux ; hors de l'en-tête général (écrans sans Layout), elles restent visibles.
export default function NotificationBell(props) {
  const sousEnteteGlobal = useContext(EnteteGlobaleContext);
  if (sousEnteteGlobal && props.emplacement !== 'entete') return null;
  return <NotificationBellInterne {...props} />;
}

function NotificationBellInterne({ couleurIcone = '#fff', taille = 22 }) {
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [nonLues,       setNonLues]       = useState(0);
  const [panneauOuvert, setPanneauOuvert] = useState(false);
  const [prefs, setPrefs] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('sogas_notif_prefs') || '{"son":true,"vibration":true}');
    } catch { return { son: true, vibration: true }; }
  });

  const prevNonLues = useRef(-1);  // -1 = premier chargement, pas de son
  const aInteragi   = useRef(false);
  const bellRef     = useRef(null);
  const intervalRef = useRef(null);
  const token       = localStorage.getItem('sogas_token');
  const headers     = { Authorization: `Bearer ${token}` };

  // Marquer que l'utilisateur a interagi
  useEffect(() => {
    const h = () => { aInteragi.current = true; };
    document.addEventListener('click', h, { once: true });
    return () => document.removeEventListener('click', h);
  }, []);

  // ── Fetch ────────────────────────────────────────────────────────────────
  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/api/notifications`, { headers });
      const data = res.data;
      const newNonLues = data.non_lues;
      // Tri strict created_at DESC côté frontend (indépendant du tri API)
      const sorted = [...data.notifications].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      setNotifications(sorted);
      setNonLues(newNonLues);

      // Son + vibration si nouvelles notifs (pas au premier chargement)
      if (
        prevNonLues.current >= 0 &&
        newNonLues > prevNonLues.current &&
        aInteragi.current &&
        document.visibilityState === 'visible'
      ) {
        const hasUrgente = data.notifications.some(n => !n.lu && n.priorite === 'urgente');
        if (prefs.son)       jouerBip(hasUrgente);
        if (prefs.vibration) vibrer(hasUrgente);

        // Push navigateur si urgente
        if (hasUrgente && Notification.permission === 'granted') {
          const n = data.notifications.find(n => !n.lu && n.priorite === 'urgente');
          if (n) {
            const push = new Notification(n.titre, {
              body: n.message, icon: '/logovert.png', tag: n.id,
            });
            push.onclick = () => { window.focus(); if (n.lien) navigate(n.lien); push.close(); };
          }
        }
      }
      prevNonLues.current = newNonLues;
    } catch (_) {}
  }, [token, prefs]); // eslint-disable-line

  // Polling 10 s
  useEffect(() => {
    fetchNotifications();
    intervalRef.current = setInterval(fetchNotifications, 10_000);
    return () => clearInterval(intervalRef.current);
  }, [fetchNotifications]);

  // Demande permission push au premier rendu
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      setTimeout(() => Notification.requestPermission(), 4000);
    }
  }, []);

  // Fermer au clic extérieur
  useEffect(() => {
    if (!panneauOuvert) return;
    const h = (e) => {
      if (bellRef.current && !bellRef.current.contains(e.target)) {
        setPanneauOuvert(false);
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [panneauOuvert]);

  // ── Actions ──────────────────────────────────────────────────────────────
  const marquerLue = async (notif) => {
    if (!notif.lu) {
      await axios.put(`${API}/api/notifications/${notif.id}/lire`, {}, { headers }).catch(() => {});
      setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, lu: true } : n));
      setNonLues(prev => Math.max(0, prev - 1));
    }
    if (notif.lien) { setPanneauOuvert(false); navigate(notif.lien); }
  };

  const toutMarquerLu = async () => {
    await axios.put(`${API}/api/notifications/lire-tout`, {}, { headers }).catch(() => {});
    setNotifications(prev => prev.map(n => ({ ...n, lu: true })));
    setNonLues(0);
  };

  const effacerLues = async () => {
    await axios.delete(`${API}/api/notifications/lues`, { headers }).catch(() => {});
    setNotifications(prev => prev.filter(n => !n.lu));
  };

  const supprimer = async (e, id) => {
    e.stopPropagation();
    await axios.delete(`${API}/api/notifications/${id}`, { headers }).catch(() => {});
    const removed = notifications.find(n => n.id === id);
    setNotifications(prev => prev.filter(n => n.id !== id));
    if (removed && !removed.lu) setNonLues(prev => Math.max(0, prev - 1));
  };

  const sauverPrefs = (np) => {
    setPrefs(np);
    localStorage.setItem('sogas_notif_prefs', JSON.stringify(np));
  };

  const badgeLabel = nonLues > 9 ? '9+' : nonLues;

  // ── Groupes affichés (max 20 au total) ───────────────────────────────────
  const notificationsAffichees = notifications.slice(0, 20);
  const nonLuesList = notificationsAffichees.filter(n => !n.lu);
  const luesList    = notificationsAffichees.filter(n => n.lu);
  const plusDeVingt = notifications.length > 20;
  const aDesLues    = notifications.some(n => n.lu);

  // ── Render d'un item notification ─────────────────────────────────────────
  const renderItem = (notif, attenued = false) => (
    <div
      key={notif.id}
      className="notif-item"
      onClick={() => marquerLue(notif)}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '10px 12px',
        background: notif.lu ? '#FAFAFA' : (BG_TYPE[notif.type] || '#F8FFF8'),
        borderBottom: '1px solid #F5F5F5',
        cursor: notif.lien ? 'pointer' : 'default',
        borderLeft: notif.priorite === 'urgente'
          ? `3px solid ${ROUGE}`
          : notif.lu ? '3px solid transparent' : `3px solid ${VERT}`,
        transition: 'background 0.12s',
        position: 'relative',
        opacity: attenued ? 0.65 : 1,
      }}
    >
      {/* Icône type */}
      <div style={{
        width: 28, height: 28, borderRadius: '50%',
        background: notif.lu ? '#EBEBEB' : (BG_TYPE[notif.type] + 'CC' || '#E8F5E9CC'),
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, marginTop: 1,
        border: `1px solid ${notif.priorite === 'urgente' ? ROUGE + '44' : '#E5E5E5'}`,
      }}>
        {ICON_TYPE[notif.type] || <Bell size={13} color="#999" />}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 12, fontWeight: notif.lu ? 500 : 700,
          color: notif.lu ? '#666' : '#1a1a1a', marginBottom: 2,
          display: 'flex', alignItems: 'center', gap: 5,
        }}>
          <span style={{
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
          }}>
            {notif.titre}
          </span>
          {!notif.lu && (
            <span style={{
              width: 6, height: 6, borderRadius: '50%',
              background: VERT, flexShrink: 0,
            }} />
          )}
        </div>
        <div style={{
          fontSize: 14, color: '#888', lineHeight: 1.4,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          marginBottom: 3,
        }}>
          {notif.message}
        </div>
        <div style={{ fontSize: 12, color: '#bbb' }}>
          {tempsRelatif(notif.created_at)}
        </div>
      </div>

      {/* Supprimer */}
      <button
        className="notif-del"
        onClick={(e) => supprimer(e, notif.id)}
        title="Supprimer"
        style={{
          background: 'none', border: 'none',
          cursor: 'pointer', padding: 3,
          opacity: 0, color: '#ccc', flexShrink: 0,
          borderRadius: 4,
          display: 'flex', alignItems: 'center',
          transition: 'opacity 0.12s, color 0.12s',
        }}
      >
        <Trash2 size={11} />
      </button>
    </div>
  );

  return (
    <>
      <style>{`
        @keyframes notif-pulse {
          0%,100% { transform: scale(1);   opacity: 1; }
          50%      { transform: scale(1.35); opacity: 0.85; }
        }
        @keyframes notif-slide {
          from { opacity: 0; transform: translateY(-6px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)   scale(1);    }
        }
        @keyframes bell-shake {
          0%,100%{ transform:rotate(0);   }
          15%    { transform:rotate(14deg);}
          30%    { transform:rotate(-12deg);}
          45%    { transform:rotate(8deg); }
          60%    { transform:rotate(-5deg);}
        }
        .notif-item:hover { background: #F0F0F0 !important; }
        .notif-item:hover .notif-del { opacity: 1 !important; }
        @media (hover: none) { .notif-del { opacity: 0.55 !important; } }
      `}</style>

      <div ref={bellRef} style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>

        {/* ── Bouton cloche ──────────────────────────────────────────────── */}
        <button
          onClick={() => { aInteragi.current = true; setPanneauOuvert(v => !v); }}
          aria-label={`Notifications${nonLues > 0 ? ` (${nonLues} non lues)` : ''}`}
          style={{
            background: 'rgba(255,255,255,0.15)',
            border: 'none',
            borderRadius: '50%',
            width: taille + 18,
            height: taille + 18,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            flexShrink: 0,
            animation: nonLues > 0 ? 'bell-shake 2.5s ease-in-out 1.5s' : 'none',
            transition: 'background 0.15s',
          }}
        >
          <Bell size={taille} color={couleurIcone} />

          {/* ── Badge ───────────────────────────────────────────────────── */}
          {nonLues > 0 && (
            <span style={{
              position: 'absolute',
              top: -1, right: -1,
              minWidth: 17, height: 17,
              borderRadius: 9,
              background: ROUGE,
              color: '#fff',
              fontSize: 9,
              fontWeight: 800,
              lineHeight: '17px',
              textAlign: 'center',
              padding: '0 4px',
              boxShadow: '0 0 0 2px rgba(0,0,0,0.25)',
              animation: 'notif-pulse 2s ease-in-out infinite',
              pointerEvents: 'none',
            }}>
              {badgeLabel}
            </span>
          )}
        </button>

        {/* ── Panneau ────────────────────────────────────────────────────── */}
        {panneauOuvert && (
          <div style={{
            position: 'fixed',
            top: (() => {
              const el = bellRef.current;
              if (!el) return 60;
              const rect = el.getBoundingClientRect();
              return rect.bottom + 8;
            })(),
            right: (() => {
              const el = bellRef.current;
              if (!el) return 8;
              const rect = el.getBoundingClientRect();
              return Math.max(8, window.innerWidth - rect.right);
            })(),
            width: Math.min(320, window.innerWidth - 16),
            maxHeight: 460,
            background: '#fff',
            borderRadius: 14,
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
            border: '1px solid #E5E5E5',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 99999,
            animation: 'notif-slide 0.18s ease',
            overflow: 'hidden',
          }}>

            {/* En-tête panneau */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '11px 14px',
              borderBottom: '1px solid #F0F0F0',
              flexShrink: 0,
              background: '#FAFAFA',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Bell size={13} color={VERT} />
                <span style={{ fontWeight: 800, fontSize: 13, color: '#1a1a1a' }}>
                  Notifications
                </span>
                {nonLues > 0 && (
                  <span style={{
                    background: ROUGE, color: '#fff',
                    borderRadius: 10, fontSize: 9, fontWeight: 800,
                    padding: '1px 6px', lineHeight: '14px',
                  }}>{badgeLabel}</span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                {/* Toggle son */}
                <button
                  onClick={() => sauverPrefs({ ...prefs, son: !prefs.son })}
                  title={prefs.son ? 'Son : activé' : 'Son : désactivé'}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    opacity: prefs.son ? 1 : 0.35, padding: '2px 4px',
                    display: 'flex', alignItems: 'center',
                  }}
                ><Bell size={14} color={prefs.son ? VERT : '#999'} /></button>
                {nonLues > 0 && (
                  <button
                    onClick={toutMarquerLu}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 4,
                      background: 'none', border: `1px solid ${VERT}`,
                      borderRadius: 6, cursor: 'pointer',
                      fontSize: 10, color: VERT, fontWeight: 700,
                      padding: '3px 7px',
                    }}
                  >
                    <CheckCheck size={11} /> Tout lire
                  </button>
                )}
                {aDesLues && (
                  <button
                    onClick={effacerLues}
                    title="Supprimer toutes les notifications lues"
                    style={{
                      display: 'flex', alignItems: 'center', gap: 4,
                      background: 'none', border: '1px solid #E0E0E0',
                      borderRadius: 6, cursor: 'pointer',
                      fontSize: 10, color: '#999', fontWeight: 600,
                      padding: '3px 7px',
                    }}
                  >
                    <Trash2 size={11} /> Effacer les lues
                  </button>
                )}
              </div>
            </div>

            {/* Liste */}
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {notifications.length === 0 ? (
                <div style={{
                  textAlign: 'center', padding: '32px 16px',
                  color: '#bbb', fontSize: 12,
                }}>
                  <Bell size={26} color="#ddd" style={{ display: 'block', margin: '0 auto 10px' }} />
                  Aucune notification
                </div>
              ) : (
                <>
                  {/* ── Non lues ── */}
                  {nonLuesList.length > 0 && (
                    <>
                      <div style={{
                        padding: '5px 12px 4px',
                        fontSize: 9, fontWeight: 800, color: VERT,
                        textTransform: 'uppercase', letterSpacing: '0.8px',
                        background: '#F0F7F0',
                        borderBottom: '1px solid #E8F0E8',
                      }}>
                        Nouvelles · {nonLuesList.length}
                      </div>
                      {nonLuesList.map(notif => renderItem(notif, false))}
                    </>
                  )}

                  {/* ── Lues ── */}
                  {luesList.length > 0 && (
                    <>
                      <div style={{
                        padding: '5px 12px 4px',
                        fontSize: 9, fontWeight: 700, color: '#999',
                        textTransform: 'uppercase', letterSpacing: '0.8px',
                        background: '#F5F5F5',
                        borderTop: nonLuesList.length > 0 ? '1px solid #E8E8E8' : 'none',
                        borderBottom: '1px solid #EBEBEB',
                      }}>
                        Déjà lues · {luesList.length}
                      </div>
                      {luesList.map(notif => renderItem(notif, true))}
                    </>
                  )}

                  {/* ── Limite 20 ── */}
                  {plusDeVingt && (
                    <div style={{
                      padding: '7px 14px',
                      textAlign: 'center',
                      fontSize: 10, color: '#bbb',
                      borderTop: '1px solid #F0F0F0',
                    }}>
                      Affichage limité à 20 notifications
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Pied — préfs vibration */}
            <div style={{
              padding: '7px 14px',
              borderTop: '1px solid #F0F0F0',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexShrink: 0,
              background: '#FAFAFA',
            }}>
              <button
                onClick={() => sauverPrefs({ ...prefs, vibration: !prefs.vibration })}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: 10, color: '#aaa',
                  display: 'flex', alignItems: 'center', gap: 4,
                }}
              >
                Vibration {prefs.vibration ? 'activée' : 'désactivée'}
              </button>
              <span style={{ fontSize: 10, color: '#ddd' }}>
                {notifications.length} notif{notifications.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
